import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { SavedLiveEntry } from '@/mocks/listenerCenter';

interface Props {
  lives: SavedLiveEntry[];
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  live: { label: 'EN VIVO AHORA', color: 'bg-accent-500 text-background-50', icon: 'ri-broadcast-line' },
  scheduled: { label: 'Próximamente', color: 'bg-amber-100 text-amber-700', icon: 'ri-calendar-schedule-line' },
  finished: { label: 'Finalizado', color: 'bg-foreground-100 text-foreground-600', icon: 'ri-check-double-line' },
};

export default function SavedLives({ lives }: Props) {
  const navigate = useNavigate();
  const [items, setItems] = useState(lives);

  const liveNow = items.filter((l) => l.status === 'live');
  const scheduled = items.filter((l) => l.status === 'scheduled');
  const finished = items.filter((l) => l.status === 'finished');

  const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const confirmRemove = (id: number, programName: string) => {
    Swal.fire({
      title: 'Quitar live guardado',
      text: `¿Querés eliminar "${programName}" de tus lives guardados?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, quitar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setItems(items.filter((l) => l.id !== id));
        Swal.fire({
          toast: true,
          position: 'top-end',
          title: 'Live eliminado de guardados',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
          customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
          buttonsStyling: false,
        });
      }
    });
  };

  const formatDuration = (min: number) => {
    if (min >= 60) {
      const h = Math.floor(min / 60);
      const m = min % 60;
      return m > 0 ? `${h}h ${m}min` : `${h}h`;
    }
    return `${min} min`;
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('es-MX', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderCard = (live: SavedLiveEntry) => {
    const cfg = STATUS_CONFIG[live.status] || STATUS_CONFIG.finished;
    return (
      <div
        key={live.id}
        className="bg-background-50 rounded-xl overflow-hidden group cursor-pointer"
        onClick={() => navigate(`/radio/${slug(live.stationName)}`)}
      >
        <div className="relative h-40 overflow-hidden">
          <img
            src={live.coverUrl}
            alt={live.programName}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent"></div>
          {live.status === 'live' && (
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-accent-500 rounded-full">
              <div className="w-2 h-2 bg-background-50 rounded-full animate-pulse"></div>
              <span className="text-[10px] font-bold text-background-50 whitespace-nowrap">EN VIVO</span>
            </div>
          )}
          {live.status === 'scheduled' && (
            <div className="absolute top-3 left-3">
              <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap bg-amber-100 text-amber-700">
                Próximamente
              </span>
            </div>
          )}
          {live.status === 'finished' && (
            <div className="absolute top-3 left-3">
              <span className="px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap bg-foreground-100/80 text-foreground-600">
                Finalizado
              </span>
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); confirmRemove(live.id, live.programName); }}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-950/40 hover:bg-foreground-950/60 text-background-50 cursor-pointer transition-all opacity-0 group-hover:opacity-100"
            title="Quitar de guardados"
          >
            <i className="ri-close-line"></i>
          </button>
          <div className="absolute bottom-3 left-3 right-3">
            <h4 className="text-sm font-bold text-background-50 leading-tight">{live.programName}</h4>
          </div>
        </div>
        <div className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <img
              src={live.stationLogo}
              alt={live.stationName}
              className="w-7 h-7 rounded-lg object-cover object-top shrink-0"
            />
            <span className="text-xs font-semibold text-foreground-800 truncate">{live.stationName}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <i className="ri-user-voice-line text-accent-500"></i>
            <span>{live.hostName}</span>
            <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
            <i className="ri-time-line"></i>
            <span>{formatDuration(live.durationMinutes)}</span>
          </div>
          {live.status === 'scheduled' && (
            <div className="flex items-center gap-1.5 mt-2 text-[10px] text-foreground-400">
              <i className="ri-calendar-line"></i>
              <span>Guardado el {formatDate(live.savedAt)}</span>
            </div>
          )}
          {live.status === 'finished' && (
            <div className="flex items-center gap-1.5 mt-2 text-[10px] text-foreground-400">
              <i className="ri-check-double-line"></i>
              <span>Transmitido • Guardado {formatDate(live.savedAt)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* En vivo ahora */}
      {liveNow.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2.5 h-2.5 bg-accent-500 rounded-full animate-pulse"></div>
            <h3 className="text-sm font-semibold text-foreground-950">En Vivo Ahora ({liveNow.length})</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
            {liveNow.map(renderCard)}
          </div>
        </div>
      )}

      {/* Próximos */}
      {scheduled.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground-950 mb-4">
            Próximos Lives ({scheduled.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
            {scheduled.map(renderCard)}
          </div>
        </div>
      )}

      {/* Finalizados */}
      {finished.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground-950 mb-4">
            Lives Finalizados ({finished.length})
          </h3>
          <div className="space-y-2">
            {finished.map((live) => (
              <div
                key={live.id}
                className="bg-background-50 rounded-xl p-4 flex items-center gap-4 group cursor-pointer hover:bg-background-100/80 transition-all"
                onClick={() => navigate(`/radio/${slug(live.stationName)}`)}
              >
                <img
                  src={live.coverUrl}
                  alt={live.programName}
                  className="w-14 h-14 rounded-lg object-cover object-top shrink-0 opacity-75"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-foreground-950">{live.programName}</h4>
                  <div className="flex items-center gap-2 text-xs text-foreground-500 mt-0.5">
                    <span>{live.stationName}</span>
                    <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                    <span>{live.hostName}</span>
                  </div>
                  <div className="text-[10px] text-foreground-400 mt-1">{formatDuration(live.durationMinutes)} • {formatDate(live.savedAt)}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); confirmRemove(live.id, live.programName); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-200 cursor-pointer transition-all opacity-0 group-hover:opacity-100"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div className="bg-background-50 rounded-2xl p-10 text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-rose-100 flex items-center justify-center">
            <i className="ri-live-line text-2xl text-rose-400"></i>
          </div>
          <p className="text-foreground-600 text-sm">No tenés lives guardados</p>
          <p className="text-foreground-400 text-xs mt-1">Guardá un live en vivo o programado para verlo aquí</p>
        </div>
      )}
    </div>
  );
}